'use strict';

import React, { PropTypes } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash/fp';

require('styles/PreciseScroller.css');

const requestAnimFrame =
  window.requestAnimationFrame       ||
  window.webkitRequestAnimationFrame ||
  window.mozRequestAnimationFrame    ||
  function( callback ){
    window.setTimeout(callback, 1000 / 60);
  };

class LineComponent extends React.Component {
  // constructor
  constructor(props) {
    super(props);
  }

  // static properties
  static LineStates = {
    INIT: 0,
    MOVE: 1,
    ANIMATE: 2
  }

  static propTypes = {
    content: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
    ]).isRequired,
    style: PropTypes.object.isRequired,
    callback: PropTypes.func.isRequired,
    state: PropTypes.oneOf(_.values(LineComponent.LineStates)).isRequired
  }

  // private methods
  measure(cb) {
    const container = ReactDOM.findDOMNode(this.refs.lineContainer);
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      // hack for safari
      setTimeout(this.measure.bind(this, cb), 10);
      return;
    }

    cb({
      height: container.offsetHeight,
      width: container.offsetWidth
    });
  }

  // react callbacks
  componentDidMount() {
    this.measure(this.props.callback);
  }

  componentDidUpdate(prevProps) {
    if (this.props.state === LineComponent.LineStates.MOVE &&
        prevProps.state  === LineComponent.LineStates.INIT) {
      requestAnimFrame(this.props.callback);
    }
  }

  render() {
    return (
      <div ref="lineContainer"
           className="precise-scroller-line"
           style={this.props.style}>
        {this.props.content}
      </div>
    );
  }
}

class PreciseScrollerComponent extends React.Component {
  // construction
  constructor(props) {
    super(props);
    this.index = 0;
    this.state = {
      lines: {}
    };
    this.unmounting = false;
  }

  dimensions = {}

  initialLineStyle = {
    position: 'absolute',
    visibility: 'hidden',
    opacity: 0
  }

  // static properties
  static propTypes = {
    stream: PropTypes.object.isRequired,
    duration: PropTypes.number.isRequired,
    direction: PropTypes.oneOf(['up', 'down', 'left', 'right']),
    gap: PropTypes.number,
    lineLimit: PropTypes.number,
    className: PropTypes.string
  }

  static defaultProps = {
    direction: 'left',
    gap: 10,
    lineLimit: 100
  }

  static displayName = 'PreciseScrollerComponent'

  // private methods
  nextIndex() {
    const index = this.index;
    this.index = (this.index + 1) % this.props.lineLimit;
    return index;
  }

  measureContainer() {
    const container = ReactDOM.findDOMNode(this.refs.container);

    this.dimensions = {
      height: container.offsetHeight,
      width: container.offsetWidth
    };
    this.velocity = {
      x: this.dimensions.width / this.props.duration,
      y: this.dimensions.height / this.props.duration
    };

    return this.dimensions;
  }

  delayFor(dimensions) {
    const {direction} = this.props;
    const vertical = direction === 'up' || direction === 'down';
    const velocity = vertical ? this.velocity.y : this.velocity.x;
    let pixels = vertical ? dimensions.height : dimensions.width;
    pixels += this.props.gap;

    return 1 / (velocity / pixels);
  }

  initialTransformations = {
    left: () => {
      const x = this.dimensions.width;
      return {
        transform: `translateX(${x}px)`
      };
    },
    right: (dimensions) => {
      const x = dimensions.width;
      return {
        transform: `translateX(-${x}px)`
      };
    },
    up: () => {
      const y = this.dimensions.height;
      return {
        transform: `translateY(${y}px)`
      };
    },
    down: (dimensions) => {
      const y = dimensions.height;
      return {
        transform: `translateY(-${y}px)`
      };
    }
  }

  getInitialTransformation() {
    return this.initialTransformations[this.props.direction];
  }

  finalTransformations = {
    left: (dimensions) => {
      const duration = this.props.duration + this.delayFor(dimensions);
      const x = dimensions.width;
      return {
        transform: `translateX(-${x}px)`,
        transitionDuration: `${duration}s`,
        transitionTimingFunction: 'linear'
      };
    },
    right: (dimensions) => {
      const duration = this.props.duration + this.delayFor(dimensions);
      const x = this.dimensions.width;
      return {
        transform: `translateX(${x}px)`,
        transitionDuration: `${duration}s`,
        transitionTimingFunction: 'linear'
      };
    },
    up: (dimensions) => {
      const duration = this.props.duration + this.delayFor(dimensions);
      const y = dimensions.height;
      return {
        transform: `translateY(-${y}px)`,
        transitionDuration: `${duration}s`,
        transitionTimingFunction: 'linear'
      };
    },
    down: (dimensions) => {
      const duration = this.props.duration + this.delayFor(dimensions);
      const y = this.dimensions.height;
      return {
        transform: `translateY(${y}px)`,
        transitionDuration: `${duration}s`,
        transitionTimingFunction: 'linear'
      };
    }
  }

  getFinalTransformation() {
    return this.finalTransformations[this.props.direction];
  }

  removeLine(index) {
    const lines = Object.assign({}, this.state.lines);
    delete lines[index];

    this.setState({lines});
  }

  animateLine(index) {
    if (this.unmounting) {
      return;
    }

    const line = Object.assign({}, this.state.lines[index]);
    const transformation = this.getFinalTransformation();
    const style = Object.assign(
      {position: 'absolute'},
      transformation(line.dimensions)
    );
    const state = LineComponent.LineStates.ANIMATE;
    Object.assign(
      line,
      {style, state},
    );

    const lines = Object.assign({}, this.state.lines);
    lines[index] = line;

    this.setState({lines});


    const delay = this.delayFor(line.dimensions);
    const delayMs = Math.ceil(delay * 1000);
    const durationMs = Math.ceil(this.props.duration * 1000);

    setTimeout(this.removeLine.bind(this, index), durationMs + delayMs);
    setTimeout(this.nextLine.bind(this), delayMs);
  }

  translateLine(index, dimensions) {
    if (this.unmounting) {
      return;
    }

    const transformation = this.getInitialTransformation();
    const style = Object.assign(
      {position: 'absolute'},
      transformation(dimensions)
    );
    const state = LineComponent.LineStates.MOVE;
    const line = Object.assign(
      {},
      this.state.lines[index],
      {dimensions, style, state},
      {callback: this.animateLine.bind(this, index)}
    );

    const newLines = Object.assign({}, this.state.lines);
    newLines[index] = line;

    this.setState({lines: newLines});
  }

  nextLine() {
    const index = this.nextIndex();
    const nextVal = this.props.stream.next();

    if (nextVal.done) {
      return;
    }

    const line = {
      state: LineComponent.LineStates.INIT,
      content: nextVal.value,
      key: index,
      style: this.initialLineStyle,
      callback: this.translateLine.bind(this, index)
    };
    const newLines = Object.assign({}, this.state.lines);
    newLines[index] = line;

    this.setState({lines: newLines});
  }

  initializeScroller() {
    this.nextLine();
  }

  // react callbacks
  componentDidMount() {
    // measure
    this.measureContainer();

    // init
    this.initializeScroller();
  }

  componentDidUpdate() {
    // measure
    this.measureContainer();
  }

  componentWillUnmount() {
    this.unmounting = true;
  }

  render() {
    const classNames = ['precise-scroller'];
    if (this.props.className) {
      classNames.push(this.props.className);
    }

    const lines = _.values(this.state.lines)
      .map(props => <LineComponent {...props} />);

    return (
      <div ref="container" className={classNames.join(' ')}>
        {lines}
      </div>
    );
  }
}

export default PreciseScrollerComponent;



// WEBPACK FOOTER //
// ./src/components/PreciseScrollerComponent.js